import { ICompany, IUserEmail, IUserProfile } from "@redbelly-tokeniser/mystake-api-sdk";
import {
  CompanyListItemStatus,
  IUserRolesByCompany,
  Roles,
  ShareholderStatus,
} from "@redbelly-tokeniser/mystake-common-sdk";
import { Logger } from "@redbelly-tokeniser/mystake-logger-sdk";
import React, { useCallback } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { AuthContext } from "../auth/AuthProvider";
import { IMatchParams, NavigationSources, PageRoutes, UserStatus } from "../constants/constants";
import { companyService, userService } from "../util";
import { adminOnboardingDestination } from "./createAdminAccount/adminOnboardingDestination";
import { getUniqueCompanyCount } from "./utility/helpers";

export const logger = new Logger("console", { level: "error" });

const RETURN_TO_PARAM = "returnTo";

/**
 * Reads and validates the `returnTo` query param set by RouteWithSubRoutes when an
 * unauthenticated user was redirected away from a private deep-link. Rejects anything
 * that isn't a same-origin relative path so it can't be used as an open redirect.
 */
const getReturnToPath = (search: string): string | null => {
  if (!search) {
    return null;
  }

  // URLSearchParams#get already decodes percent-encoding, so no further decoding is needed here.
  const returnTo = new URLSearchParams(search).get(RETURN_TO_PARAM);

  if (!returnTo) {
    return null;
  }

  if (
    !returnTo.startsWith("/") ||
    returnTo.startsWith("//") ||
    returnTo.includes("://") ||
    returnTo.startsWith("/signin")
  ) {
    return null;
  }

  return returnTo;
};

// Matches every company-scoped route prefix defined in RoutesConfig.ts that takes a companyId
// as its first path segment (either nested under /company/ or /investor/company/, or one of the
// handful of top-level onboarding routes like /add-payment/:companyId).
const extractCompanyIdFromPath = (pathName: string): string | undefined =>
  pathName.match(
    /^(?:company|add-company|investor\/company|nominee\/authorise-mystake-final-step|authorise-mystake-final-step|authorise-mystake|add-payment)\/([^/?#]+)/
  )?.[1];

interface IRedirectLookup {
  [key: string]: {
    url: string;
    relatedURLs: string[];
  };
}

const redirectLookup: IRedirectLookup = {
  emailNotVerified: {
    url: "/verify-email",
    relatedURLs: [],
  },
  verifyIDPending: {
    url: "/verify-id",
    relatedURLs: ["email-verified", "verify-email/:invalid", "invalid-token"],
  },
};

const getUserStatus = (user: IUserProfile, pathName: string) => {
  const isEmailVerified = user.emails?.find((email: IUserEmail) => email.isPrimary)?.isVerified || false;

  if (!isEmailVerified) {
    return "emailNotVerified";
  }

  if (
    ((!user.birthDetails?.dob || !user.kycDetails?.address) && !user.isInvestor) ||
    ((!user.birthDetails?.dob || !user.kycDetails?.address) &&
      (pathName.substring(0, 18) === "investor/verify-id" || pathName.substring(0, 18) === "wallet/verify-id") &&
      user.isInvestor)
  ) {
    return "verifyIDPending";
  }

  return "done";
};

export const CheckOnboardingFlow: React.FC = () => {
  logger.log("info", "CheckOnboardingFlow entry");
  const { currentUser, getInvestors, getUpdatedUser, setExternalInvestor } = React.useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();

  const match = useRouteMatch<IMatchParams>("/company/:companyId");
  const matchAddCompany = useRouteMatch<IMatchParams>("/add-company/:companyId");
  const matchNomineeCompany = useRouteMatch<IMatchParams>("/nominee/authorise-mystake-final-step/:companyId");
  const matchInvestorCompany = useRouteMatch<IMatchParams>("/investor/company/:companyId");
  const matchSignin = useRouteMatch("/signin");

  const companyIdParam =
    match?.params?.companyId ||
    matchAddCompany?.params?.companyId ||
    matchNomineeCompany?.params?.companyId ||
    matchInvestorCompany?.params?.companyId;

  let acnParam: string | null = null;
  let returnToParam: string | null = null;

  if (matchSignin && location.search) {
    const queryParams = new URLSearchParams(location.search.toLowerCase());
    // Emails for external users are verified during sign in
    acnParam = queryParams.get("acn");

    returnToParam = getReturnToPath(location.search);
  }

  const redirectToSwitchCompany = useCallback(
    () => history.push(PageRoutes.SWITCH_COMPANY, { base: NavigationSources.fromSignIn }),
    [history]
  );

  const redirectRoute = useCallback(
    async (pathname: string) => {
      logger.log("info", "CheckOnboardingFlow entering redirectRoute");
      try {
        const pathName = pathname.replace(/^\/+/g, "");

        const verifyIdPages = [
          "verify-id",
          "admin/verify-id",
          "investor/verify-id",
          "wallet/verify-id",
          "nominee/verify-id",
        ];
        if (verifyIdPages.includes(pathName)) {
          logger.log("info", "CheckOnboardingFlow - already on verify-id page, skipping redirect check");
          return;
        }

        if (!currentUser) {
          return;
        }

        let currentUserUpdatedInfo = currentUser;

        const isFromSignin = pathName === "signin";
        const needsFreshData =
          !isFromSignin &&
          currentUser.isExternalInvestor &&
          (pathName.includes("investor") || pathName.includes("switch-company") || pathName === "");

        if (needsFreshData) {
          const freshUserData = await getUpdatedUser();
          if (freshUserData) {
            currentUserUpdatedInfo = freshUserData;
          }
        }

        const userStatus = getUserStatus(currentUserUpdatedInfo, pathName);

        const doNotRedirectUrls = [
          "user/confirm-email-update",
          "user/cancel-email-update",
          "investor/create-account",
          "admin/create-account",
          "nominee/create-account",
          "invite/averer",
          "email-verified",
          "forgot-password",
          "reset-password",
          "password-updated",
        ];

        if (doNotRedirectUrls.includes(pathName) || (pathName === "signin" && !currentUser)) {
          return;
        }

        const commonURLs = [
          "wallet",
          "wallet/kyc-done",
          "account",
          "add-company",
          "switch-company",
          "nominee-account-created",
        ];

        const verifyIdURLs = [
          "verify-id",
          "admin/verify-id",
          "wallet/verify-id",
          "email-verified",
          "verify-email",
          "invalid-token",
          "invitation-revoked",
        ];

        const isEmailVerified =
          currentUserUpdatedInfo.emails?.find((email: IUserEmail) => email.isPrimary)?.isVerified || false;

        const verificationRedirect = (prefix = "") => {
          const { url, relatedURLs } = redirectLookup[userStatus];

          const fullURL = prefix ? `/${prefix}${url}` : url;

          const additionalRelatedURLs = prefix ? relatedURLs.map((relatedURL) => `${prefix}/${relatedURL}`) : [];

          const allURLs = [...relatedURLs, ...additionalRelatedURLs];

          if (!allURLs.includes(pathName)) {
            history.push(fullURL);
          }
        };

        const isCompanyIdValid = (companyId: string, companyIdArray: string[]) => {
          if (companyIdArray.includes(companyId)) {
            return true;
          }

          return false;
        };

        const companyRedirectFlow = (companyInfo: ICompany) => {
          switch (companyInfo.status) {
            case CompanyListItemStatus.AddPaymentMethod:
              if (!pathName.includes("add-payment")) {
                history.push(`/add-payment/${companyInfo._id}`);
              }

              break;
            case CompanyListItemStatus.KYCPending:
              logger.log("info", "CheckOnboardingFlow KYC Pending");
              // check sub cases
              if (
                // nominee is other than admin
                companyInfo.kycAuthorization?.hasNominated &&
                companyInfo.kycAuthorization.nomineeId !== currentUserUpdatedInfo?._id &&
                ![...commonURLs, "admin-view"].includes(pathName)
              ) {
                logger.log("info", "CheckOnboardingFlow KYC PENDING - showing admin view");
                // show admin view for first-company (will see the awaiting authorisation screen)
                history.push(`/company/${companyInfo._id}/admin-view`);
              } else if (
                // nominee is also admin / has not finished kyc
                companyInfo.kycAuthorization?.hasNominated &&
                companyInfo.kycAuthorization.nomineeId === currentUserUpdatedInfo?._id &&
                userStatus !== UserStatus.DONE
              ) {
                verificationRedirect();
              } else if (
                // nominee is also admin / kyc using greenid is done
                companyInfo.kycAuthorization?.hasNominated &&
                companyInfo.kycAuthorization.nomineeId === currentUserUpdatedInfo?._id &&
                userStatus === UserStatus.DONE
              ) {
                logger.log("info", "CheckOnboardingFlow KYC PENDING - redirect to authorise");
                history.push(`/authorise-mystake-final-step/${companyInfo._id}`);
              } else if (!companyInfo?.kycAuthorization || !companyInfo.kycAuthorization.hasNominated) {
                logger.log("info", "CheckOnboardingFlow KYC PENDING - redirect to select nominee");
                // nominee selection page
                history.push(`/authorise-mystake/${companyInfo._id}`);
              }
              logger.log("info", "CheckOnboardingFlow KYC PENDING - none of the above - break.");
              break;
            case CompanyListItemStatus.SetUpInProgress:
            case CompanyListItemStatus.ReadyForReview:
            case CompanyListItemStatus.Active:
              // Invited administrators skip inline KYC during onboarding
              if (![...commonURLs, "authorised-account-created"].includes(pathName) && !/^company\//.test(pathName)) {
                history.push(pickDestination(adminOnboardingDestination(companyInfo._id), companyInfo._id));
              }

              break;
            default:
              break;
          }
        };

        const nomineeRedirectFlow = (companyInfo: ICompany) => {
          switch (companyInfo.status) {
            case CompanyListItemStatus.KYCPending:
              history.push(`/nominee/authorise-mystake-final-step/${companyInfo._id}`);
              break;
            case CompanyListItemStatus.SetUpInProgress:
            case CompanyListItemStatus.ReadyForReview:
            case CompanyListItemStatus.Active:
              if (
                ![
                  ...commonURLs,
                  ...verifyIdURLs,
                  "authorised-account-created",
                  "nominee/authorised-account-created",
                ].includes(pathName) &&
                pathName !== "" &&
                !/^$|^company\//.test(pathName) &&
                !(currentUserUpdatedInfo.isInvestor === true && /^investor\/company\//.test(pathName))
              ) {
                history.push(
                  pickDestination(`/company/${nomineeForCompanyIds[0]}/admin-view`, nomineeForCompanyIds[0])
                );
              }

              break;
            default:
              break;
          }
        };

        const investorRedirectFlow = async (userProfile: IUserProfile, acn: string | null) => {
          const shareholdings = userProfile.shareholdings || [];
          const hasHoldings = shareholdings.length > 0;

          const shareholderId = hasHoldings ? shareholdings[0].shareholderId : undefined;
          const companyId = userProfile.companyIds ? userProfile.companyIds[0] : undefined;
          /**
           * External users are users registering interest in a company via the Liquidise website
           */
          const isExternalUser = !!acn;

          if (isExternalUser) {
            try {
              const onboardingCompanyId = (await companyService.getCompanyByAcn(acn)).payload;
              const hasHoldingsInCompany = hasHoldings && shareholdings[0].companyId === onboardingCompanyId;

              if (hasHoldingsInCompany) {
                history.push(
                  pickDestination(
                    `/investor/company/${onboardingCompanyId}/investor-view/${shareholderId}`,
                    onboardingCompanyId
                  )
                );
              } else {
                //  Assigns 'EXTERNAL_INVESTOR' role, providing access to company info while creating investor profiles
                try {
                  if (onboardingCompanyId) {
                    await setExternalInvestor(acn, onboardingCompanyId);
                  }

                  if (hasHoldings) {
                    history.push(`/investor/company/${onboardingCompanyId}/investor-profiles/existing`, {
                      base: NavigationSources.fromSignIn,
                    });
                  } else {
                    history.push(`/investor/company/${onboardingCompanyId}/investor-profiles/add`, {
                      base: NavigationSources.fromSignIn,
                    });
                  }
                } catch {
                  redirectToSwitchCompany();
                }
              }
            } catch {
              redirectToSwitchCompany();
            }
          } else if (hasHoldings) {
            history.push(pickDestination(`/investor/company/${companyId}/investor-view/${shareholderId}`, companyId));
          } else if (currentUser?.isExternalInvestor) {
            history.push(pickDestination(`/investor/company/${companyId}/investor-profiles/active`, companyId));
          } else {
            goToSwitchCompanyOrReturnTo();
          }
        };

        // check if current user is a nominee and not admin for the company
        const nomineeResult = await userService.isNominee();
        const isNominee = nomineeResult?.isNominee ?? false;
        const nomineeForCompanyIds = nomineeResult?.nomineeForCompanyIds ?? [];

        // A deep-link the user was sent to before authenticating (see RouteWithSubRoutes) is only
        // trusted once we know it targets a company (if any) the user can actually access.
        const resolvedReturnTo = ((): string | null => {
          if (!returnToParam) {
            return null;
          }

          const returnToCompanyId = extractCompanyIdFromPath(returnToParam.replace(/^\/+/, ""));

          if (!returnToCompanyId) {
            return returnToParam;
          }

          const isAuthorized =
            isCompanyIdValid(returnToCompanyId, currentUserUpdatedInfo.companyIds) ||
            isCompanyIdValid(returnToCompanyId, nomineeForCompanyIds);

          return isAuthorized ? returnToParam : null;
        })();

        // When the user has no company context on the current URL (e.g. still on /signin), fall
        // back to the returnTo's target company so mandatory per-company checks below (KYC
        // pending, add-payment) still run for that company before any deep-link is honored.
        const effectiveCompanyIdParam =
          companyIdParam ??
          (resolvedReturnTo ? extractCompanyIdFromPath(resolvedReturnTo.replace(/^\/+/, "")) : undefined);

        // Substitutes the default "no other business rule applies" landing page below with the
        // originally requested deep-link. Mandatory steps (KYC pending, verify-id, green-id,
        // add-payment, switch-company for an unauthorized company, etc.) are never overridden,
        // since those are decided by call sites that don't use this helper.
        const pickDestination = (defaultPath: string, forCompanyId?: string): string => {
          if (!resolvedReturnTo) {
            return defaultPath;
          }

          if (!forCompanyId) {
            return resolvedReturnTo;
          }

          const returnToCompanyId = extractCompanyIdFromPath(resolvedReturnTo.replace(/^\/+/, ""));

          return !returnToCompanyId || returnToCompanyId === forCompanyId ? resolvedReturnTo : defaultPath;
        };

        const goToSwitchCompanyOrReturnTo = () => {
          if (resolvedReturnTo) {
            history.push(resolvedReturnTo);
            return;
          }

          redirectToSwitchCompany();
        };

        let totalUserCompanies = Array.from(new Set(currentUserUpdatedInfo?.companyIds)).length || 0;
        /**
         * Companies where external investors have not yet created a request should not be included in the total count.
         * As we don't have company context in onboarding, we check for investor profiles in all companies where the user is an external investor.
         */
        if (currentUserUpdatedInfo?.isExternalInvestor) {
          const externalInvestorCompanies = currentUserUpdatedInfo?.rolesByCompany.filter(
            (company: IUserRolesByCompany) => company.roles.includes(Roles.EXTERNAL_INVESTOR)
          );

          const investorProfiles = await Promise.all(
            externalInvestorCompanies.map((company: IUserRolesByCompany) =>
              getInvestors(company.companyId, 1, 10, "legalEntityName", "asc", "", true, [
                ShareholderStatus.ACCEPTED,
                ShareholderStatus.DECLINED,
                ShareholderStatus.PENDING,
              ])
            )
          );

          if (externalInvestorCompanies && investorProfiles) {
            const investorProfileCompanyCount = getUniqueCompanyCount(investorProfiles);
            const companiesWithoutRequests = externalInvestorCompanies.length - investorProfileCompanyCount;

            if (companiesWithoutRequests > 0) {
              totalUserCompanies -= companiesWithoutRequests;
            }
          }
        }

        if (isNominee) {
          logger.log("info", "CheckOnboardingFlow currentUser - isNominee");

          if (userStatus !== UserStatus.DONE) {
            verificationRedirect("nominee");
            return;
          }

          // Check that user is authorized for the companyIdParam
          if (
            companyIdParam &&
            !isCompanyIdValid(companyIdParam, nomineeForCompanyIds) &&
            !isCompanyIdValid(companyIdParam, currentUserUpdatedInfo.companyIds)
          ) {
            // eslint-disable-next-line consistent-return
            return history.push(PageRoutes.SWITCH_COMPANY);
          }

          // this scenario is for nominees who are only associated with one company - either as a nominee only, or as both a nominee and investor of the same company
          if (totalUserCompanies === 1 && nomineeForCompanyIds.length === 1) {
            // A returnTo can target a different company where this user is an admin rather than a
            // nominee (e.g. companyIds and nomineeForCompanyIds don't overlap) - route it through the
            // normal admin company flow so onboarding checks for that company still apply, instead of
            // always defaulting to the nominee company.
            const returnToCompanyId = resolvedReturnTo
              ? extractCompanyIdFromPath(resolvedReturnTo.replace(/^\/+/, ""))
              : undefined;
            const returnToTargetsAdminCompany =
              !!returnToCompanyId &&
              returnToCompanyId !== nomineeForCompanyIds[0] &&
              isCompanyIdValid(returnToCompanyId, currentUserUpdatedInfo.companyIds);
            const nomineeOrAdminCompanyId =
              returnToTargetsAdminCompany && returnToCompanyId ? returnToCompanyId : nomineeForCompanyIds[0];

            const companyInfo = (await companyService.getCompanyInfo(nomineeOrAdminCompanyId)).payload;

            if (returnToTargetsAdminCompany) {
              companyRedirectFlow(companyInfo);
            } else {
              nomineeRedirectFlow(companyInfo);
            }
            // this scenario is for nominee users who are associated with a 2 or more different companies either as an investor, admin or nominee
          } else if (totalUserCompanies > 1 && nomineeForCompanyIds.length > 0) {
            if (
              !effectiveCompanyIdParam &&
              ![...commonURLs, ...verifyIdURLs, "authorised-account-created"].includes(pathName) &&
              pathName !== "" &&
              !/^$|^company\//.test(pathName) &&
              !(currentUserUpdatedInfo?.isInvestor && /^investor\/company\//.test(pathName))
            ) {
              history.push(pickDestination(PageRoutes.SWITCH_COMPANY));
            }

            if (effectiveCompanyIdParam) {
              const companyInfo = (await companyService.getCompanyInfo(effectiveCompanyIdParam)).payload;

              if (companyInfo.status === CompanyListItemStatus.KYCPending) {
                if (
                  ![
                    ...commonURLs,
                    ...verifyIdURLs,
                    "authorised-account-created",
                    "nominee/authorised-account-created",
                    `nominee/authorise-mystake-final-step/${companyInfo._id}`,
                  ].includes(pathName) &&
                  pathName !== ""
                ) {
                  history.push(PageRoutes.SWITCH_COMPANY);
                }
              } else if (
                ![
                  ...commonURLs,
                  ...verifyIdURLs,
                  "authorised-account-created",
                  "nominee/authorised-account-created",
                ].includes(pathName) &&
                pathName !== "" &&
                !/^$|^company\//.test(pathName) &&
                !/^investor\/company\//.test(pathName)
              ) {
                history.push(pickDestination(adminOnboardingDestination(companyInfo._id), companyInfo._id));
              }
            }
          }
        } else if (
          currentUserUpdatedInfo?.isInvestor ||
          pathName.substring(0, 8) === "investor" ||
          pathName.substring(0, 6) === "wallet"
        ) {
          logger.log("info", pathName);
          logger.log("info", "CheckOnboardingFlow currentUser - isInvestor");

          if (userStatus !== UserStatus.DONE) {
            if (pathName === "account") {
              return;
            }
            verificationRedirect(pathName.substring(0, 6) === "wallet" ? "wallet" : "investor");
            return;
          }

          if (
            !["wallet", "wallet/kyc-done", "account", "switch-company"].includes(pathName) &&
            !/^investor\/company\//.test(pathName)
          ) {
            if (/^company\/.*\/investor-relations/.test(pathName)) {
              logger.info("pushed to investor portal for investor-relations");
              history.push(`/investor/${pathName}`);
              return;
            }

            if (pathName === "investor/email-verified") {
              return;
            }

            logger.log("info", "CheckOnboardingFlow - Need to change the path");
            logger.log("info", `total companies: ${totalUserCompanies}`);

            if (totalUserCompanies > 1 && !acnParam) {
              logger.info("pushed to switch company");
              goToSwitchCompanyOrReturnTo();
              return;
            }
            logger.log("info", "CheckOnboardingFlow isInvestor --> investor-view");
            investorRedirectFlow(currentUserUpdatedInfo, acnParam);
          }
        } else {
          logger.log("info", "CheckOnboardingFlow currentUser - restOfUs");

          if (!isEmailVerified) {
            if (pathName !== "account") {
              history.push("/verify-email");
            }
          } else if (totalUserCompanies === 1) {
            const companyId = currentUserUpdatedInfo?.companyIds[0];
            const companyInfo = (await companyService.getCompanyInfo(companyId as string)).payload;

            logger.log("info", `CheckOnboardingFlow company status: ${companyInfo.status}`);

            companyRedirectFlow(companyInfo);
          } else if (totalUserCompanies > 1) {
            if (
              !effectiveCompanyIdParam &&
              ![...commonURLs, ...verifyIdURLs, "authorised-account-created"].includes(pathName)
            ) {
              // eslint-disable-next-line consistent-return
              return history.push(pickDestination(PageRoutes.SWITCH_COMPANY));
            }
            if (
              effectiveCompanyIdParam &&
              !isCompanyIdValid(effectiveCompanyIdParam, currentUserUpdatedInfo.companyIds)
            ) {
              history.push(PageRoutes.SWITCH_COMPANY);
            } else if (effectiveCompanyIdParam && !pathName.includes("add-company")) {
              const companyInfo = (await companyService.getCompanyInfo(effectiveCompanyIdParam)).payload;

              companyRedirectFlow(companyInfo);
            }
          }
        }
        logger.log("info", "CheckOnboardingFlow - exit");
      } catch (error) {
        logger.log(
          "error",
          `An error occurred during execution of the redirectRoute function in the CheckOnboardingFlow component. Here is the error: ${error}`
        );
      }
    },
    [
      acnParam,
      companyIdParam,
      currentUser?.isExternalInvestor,
      getInvestors,
      getUpdatedUser,
      history,
      redirectToSwitchCompany,
      returnToParam,
      setExternalInvestor,
    ]
  );

  const isProcessingRef = React.useRef(false);
  const lastPathnameRef = React.useRef<string>("");

  React.useEffect(() => {
    // The Averer callback sends the user on to its stored return page. Running here (and holding
    // the lock below) raced that and skipped the return page's own checks.
    if (location.pathname === "/oidc/callback") {
      return;
    }

    if (isProcessingRef.current || lastPathnameRef.current === location.pathname) {
      return;
    }

    if (currentUser) {
      isProcessingRef.current = true;
      lastPathnameRef.current = location.pathname;
      logger.log("info", "CheckOnboardingFlow currentUser - redirectRoute about to enter");

      redirectRoute(location.pathname).finally(() => {
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 300);
      });
    }
  }, [currentUser?._id, location.pathname]);

  return null;
};
